import { request } from './request';
import * as PATHS from '@/constants/path';
import { URLParamBuilder } from '@/utils/functions';
import { ContactLinkPayload, ContactListModel, ContactModel, ContactPayload } from '@/typings/models/Contact';

type CustomerParams = { customerId: string };
type CustomerContactParams = CustomerParams & { contactId: string };
type LeadParams = { leadId: string };

const build = (path: string, params: Record<string, unknown> = {}, queryString = {}) =>
  URLParamBuilder(path, { params, queryString }, false);

// region Customer contacts
export function fetchCustomerContactsRequest({ params }: { params: CustomerParams }) {
  return request<ContactModel[]>(build(PATHS.CUSTOMER_CONTACTS, params), { method: 'GET' });
}

export function fetchCustomerContactRequest({ params }: { params: CustomerContactParams }) {
  return request<ContactModel>(build(PATHS.CUSTOMER_CONTACT, params), { method: 'GET' });
}

/** Creates a contact on the customer, or links an existing one of the partner (`contact_id`). */
export function createCustomerContactRequest({
  params,
  data,
}: {
  params: CustomerParams;
  data: ContactPayload | ContactLinkPayload;
}) {
  return request<ContactModel>(build(PATHS.CUSTOMER_CONTACTS, params), { method: 'POST', data });
}

export function updateCustomerContactRequest({
  params,
  data,
}: {
  params: CustomerContactParams;
  data: ContactPayload;
}) {
  return request<ContactModel>(build(PATHS.CUSTOMER_CONTACT, params), { method: 'PATCH', data });
}

/** Unlinks the contact from the customer (and all its projects); a contact left without customers is deleted. */
export function deleteCustomerContactRequest({ params }: { params: CustomerContactParams }) {
  return request<null>(build(PATHS.CUSTOMER_CONTACT, params), { method: 'DELETE' });
}

export function setCustomerPrimaryContactRequest({
  params,
  contactId,
}: {
  params: CustomerParams;
  contactId: string;
}) {
  return request<ContactModel[]>(build(PATHS.CUSTOMER_PRIMARY_CONTACT, params), {
    method: 'PUT',
    data: { contact_id: contactId },
  });
}
// endregion

// region Lead contacts — a project's contacts are its customer's; only its primary is its own.
export function fetchLeadContactsRequest({ params }: { params: LeadParams }) {
  return request<ContactModel[]>(build(PATHS.LEAD_CONTACTS, params), { method: 'GET' });
}

export function setLeadPrimaryContactRequest({
  params,
  contactId,
}: {
  params: LeadParams;
  contactId: string;
}) {
  return request<ContactModel[]>(build(PATHS.LEAD_PRIMARY_CONTACT, params), {
    method: 'PUT',
    data: { contact_id: contactId },
  });
}
// endregion

// region Partner-wide contacts
/** `GET /contacts` query. `search` applies from 3 characters (name, email, phone, company). */
export type FetchContactsQuery = {
  search?: string;
  customer_id?: string;
  /** Comma-separated `person` / `organization`. */
  customer_type?: string;
  /** 1 = only customers' primary contacts, 0 = only the others. */
  is_primary?: 0 | 1;
  sort_by?: 'created' | 'firstname' | 'lastname' | 'fullname' | 'email';
  sort_direction?: 'asc' | 'desc';
  page?: number;
  page_size?: number;
  skip_total_size?: 1;
};

export function fetchContactsRequest({ url, query = {} }: { url?: string; query?: FetchContactsQuery }) {
  return request<ContactListModel>(url || build(PATHS.CONTACTS, {}, query), {
    method: 'GET',
    absoluteURL: url,
  });
}
// endregion
